import {FindWebAudioPlayerInPoolsById, WebAudioPlayer} from "../../../Utils/Media/WebAudioPlayer.js";
import {EffectPointerPlayer} from "../../../Server/DB/@Shared/EffectPointerPlayer.js";
import {EngineConfig_Background} from "../../../Server/DB/engineConfigs/@EngineConfig/@EC_Background.js";
import {LiveSession, TriggerPackage} from "../../LiveSession.js";
import {AlarmsComp, PhaseChange_ExtraMessage} from "./AlarmsComp.js";
import {EngineSessionComp} from "./EngineSessionComp.js";
import {MediaComp} from "./MediaComp.js";
import {MicroSnoozeComp} from "./MicroSnoozeComp.js";
import {Phase} from "../../../Server/DB/@Shared/Phase.js";

export class BackgroundComp extends EngineSessionComp<EngineConfig_Background> {
	constructor(session: LiveSession, config: EngineConfig_Background) {
		super(session, config, s=>config.enabled, s=>s.IsLocal());
	}

	GetTriggerPackages() {
		return [
			new TriggerPackage("Background_IncreaseVolume", this.c.increaseVolume_triggerSet, this, {}, triggerInfo=>{
				this.SetVolume(this.volume + this.c.increaseVolume_amount);
			}),
			new TriggerPackage("Background_DecreaseVolume", this.c.decreaseVolume_triggerSet, this, {}, triggerInfo=>{
				this.SetVolume(this.volume - this.c.decreaseVolume_amount);
			}),
		];
	}

	override OnStartPhase(oldPhase: Phase, newPhase: Phase, extraMessage: PhaseChange_ExtraMessage) {
		this.UpdateEffectsEnabled();
	}
	override Unsuspend() {
		const result = super.Unsuspend();
		this.UpdateEffectsEnabled();
		return result;
	}
	override Suspend() {
		const result = super.Suspend();
		this.UpdateEffectsEnabled();
		return result;
	}

	OnStart() {
		this.SetVolume(this.c.startVolume);
		this.effectPlayer.effectPointer = this.c.effect;
		this.effectPlayer.OnStart();
		this.UpdateEffectsEnabled();
	}
	OnStop() {
		this.StopEffects();
	}

	effectPlayer = new EffectPointerPlayer();
	GetWebAudioPlayersActiveForEffectPlayer() {
		return [...this.effectPlayer.soundFile_startedPlayerIds]
			.map(id=>FindWebAudioPlayerInPoolsById(id)).filter(a=>a) as WebAudioPlayer[];
	}
	effectsPlaying = false;
	readonly volume = 0;
	SetVolume(newVolume: number) {
		this["volume" as any] = newVolume.KeepAtLeast(0); // this is the only line allowed to modify the "volume" field

		// When calling effectPlayer.Play, we only send this.volume (effect-pointer's volume multiplier gets applied internally).
		// But to update volume using underlyingWebAudioPlayer.SetVolume, we must also include effect-pointer's volume multiplier (to match).
		const volume_withEffectPointerVolumeIncluded = this.volume * (this.c.effect?.soundFile?.volume ?? 1);
		for (const player of this.GetWebAudioPlayersActiveForEffectPlayer()) {
			player.SetVolume(volume_withEffectPointerVolumeIncluded, false);
		}
	}

	UpdateEffectsEnabled() {
		if (!this.c.enabled) return;
		const baseEnabled = (()=>{
			// hidden/inferrable extra condition: base-enabled is false when comp is suspended
			if (this.IsSuspended()) return false;
			// hidden/inferrable extra condition: base-enabled is false during the tiny moment when engine is first starting (when phase = "notStarted")
			if (this.s.Comp(AlarmsComp).PhaseIs(Phase.notStarted)) return false;

			if (this.c.effectsBaseEnabledUnless_microSnoozeActive && this.s.Comp(MicroSnoozeComp).snooze_active) return false;
			if (this.c.effectsBaseEnabledUnless_mediaCompPlaying && this.s.Comp(MediaComp).uiState.isPlaying) return false;
			if (this.c.effectsBaseEnabledUnless_inPhases?.includes(this.s.Comp(AlarmsComp).phase)) return false;
			return true;
		})();
		if (baseEnabled && !this.effectsPlaying) {
			this.effectsPlaying = true;
			this.StartEffects();
		} else if (!baseEnabled && this.effectsPlaying) {
			this.effectsPlaying = false;
			this.StopEffects();
		}
	}

	StartEffects() {
		this.effectPlayer.Play(this.volume);
	}
	StopEffects() {
		this.effectPlayer.Stop();
	}
}